/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    loginUser,
    forgotPassword,
    resetPassword,
    setPassword,
    registerUser,
    twoFactorConfirmation,
    twoFactorResend,
    logoutUser,
} from './userApi';
import { UserState, UserData } from './types';

const initialState: UserState = {
    error: null,
    isFetching: false,
    isLoggedIn: false,
    showTwoFactorPage: false,
    userData: {
        email: '',
        first_name: '',
        last_name: '',
        care_home_id: 0,
        care_home_title: '',
        role: {
            id: 0,
            name: '',
            access_level: '',
            deleted: false,
        },
        zone: {
            id: 0,
            title: '',
            expiry_date: '',
            deleted: false,
        },
    },
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Login user
        builder
            .addCase(loginUser.fulfilled, (state:UserState, action) => {
                state.isFetching = false;
                // WP7 (W1): the access token is now in an HttpOnly cookie, not the
                // response body — a successful login is signalled by `user`.
                if (action.payload.user) {
                    state.userData = action.payload.user as UserData;
                    state.isLoggedIn = true;
                } else if (action.payload.twoFactorToken) {
                    state.showTwoFactorPage = true;
                }
            });

        // Forgot password
        builder
            .addCase(forgotPassword.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'A password reset email has been requested, if an account exists you will receive an email shortly.',
                });
            });

        // Reset password
        builder
            .addCase(resetPassword.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'Your password has been reset, please login with your new password.',
                });
            });

        // Set password (first time setup)
        builder
            .addCase(setPassword.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'Password has been set successfully! You can now log in.',
                });
            });

        // Register user
        builder
            .addCase(registerUser.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'Your account has been created, please login.',
                });
            });

        // Two factor confirmation
        builder
            .addCase(twoFactorConfirmation.fulfilled, (state:UserState) => {
                state.isFetching = false;
                state.isLoggedIn = true;
                state.showTwoFactorPage = false;
                notification.success({
                    message: 'Success',
                    description: 'You have successfully logged in.',
                });
            });

        // Two factor resend
        builder
            .addCase(twoFactorResend.fulfilled, (state:UserState) => {
                state.isFetching = false;
                notification.success({
                    message: 'Success',
                    description: 'A new two factor code has been sent to your email.',
                });
            });

        builder
            .addCase(logoutUser.fulfilled, (state:UserState) => {
                state.isFetching = false;
                state.isLoggedIn = false;
                state.userData = initialState.userData;
                notification.success({
                    message: 'Success',
                    description: 'You have successfully logged out.',
                });
                window.location.href = '/';
            });

        // Pending state for all requests
        builder.addMatcher(
            isAnyOf(
                loginUser.pending,
                forgotPassword.pending,
                resetPassword.pending,
                setPassword.pending,
                registerUser.pending,
                twoFactorConfirmation.pending,
                twoFactorResend.pending,
                logoutUser.pending,
            ),
            (state:UserState) => {
                state.isFetching = true;
                state.isLoggedIn = false;
                state.error = null;
                state.userData = initialState.userData;
            },
        );
        builder.addMatcher(
            isAnyOf(
                twoFactorConfirmation.pending,
                twoFactorResend.pending,
            ),
            (state:UserState) => {
                state.isFetching = true;
            },
        );

        // Error state for all requests
        builder.addMatcher(
            isAnyOf(
                loginUser.rejected,
                forgotPassword.rejected,
                resetPassword.rejected,
                setPassword.rejected,
                registerUser.rejected,
                twoFactorConfirmation.rejected,
                twoFactorResend.rejected,
                logoutUser.rejected,
            ),
            (state:UserState, action) => {
                state.isFetching = false;
                state.error = action.error;
            },
        );
    },
});

// this is for configureStore
export default userSlice.reducer;
